/**
 * IndexedDB Service for Offline Product Storage
 * Database: gct_pos_db
 */

const DB_NAME = 'gct_pos_db';
const DB_VERSION = 3; // Version 3 clears products from v2 and forces resync
const STORE_PRODUCTS = 'products';
const STORE_META = 'meta';
const STORE_OFFLINE_INVOICES = 'offlineInvoices';

class IndexedDBService {
    constructor() {
        this.db = null;
    }

    /**
     * Initialize IndexedDB
     */
    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('IndexedDB error:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('✅ IndexedDB initialized successfully');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                const oldVersion = event.oldVersion;
                const newVersion = event.newVersion;
                const tx = event.target.transaction;

                console.log(`📦 Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`);

                // Create products store
                if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
                    console.log('Creating products store...');
                    const productsStore = db.createObjectStore(STORE_PRODUCTS, { keyPath: 'id' });

                    // Create indexes
                    productsStore.createIndex('code', 'code', { unique: false });
                    productsStore.createIndex('name_unsigned', 'name_unsigned', { unique: false });
                    productsStore.createIndex('search_blob', 'search_blob', { unique: false });
                }

                // Create meta store
                if (!db.objectStoreNames.contains(STORE_META)) {
                    console.log('Creating meta store...');
                    db.createObjectStore(STORE_META, { keyPath: 'key' });
                }

                // Create offlineInvoices store (version 2)
                if (!db.objectStoreNames.contains(STORE_OFFLINE_INVOICES)) {
                    console.log('Creating offlineInvoices store...');
                    db.createObjectStore(STORE_OFFLINE_INVOICES, { keyPath: 'id' });
                }

                // If upgrading from v2 -> v3, clear products and mark resync
                if (oldVersion === 2) {
                    if (db.objectStoreNames.contains(STORE_PRODUCTS)) {
                        tx.objectStore(STORE_PRODUCTS).clear();
                    }
                    if (db.objectStoreNames.contains(STORE_META)) {
                        const metaStore = tx.objectStore(STORE_META);
                        metaStore.put({ key: 'products_need_full_resync', value: true });
                        metaStore.put({ key: 'sync_product_version', value: null });
                        metaStore.put({ key: 'sync_product_time', value: null });
                    }
                }

                console.log('✅ Database upgrade completed');
            };

            request.onblocked = () => {
                console.warn('⚠️ Database upgrade blocked. Please close all other tabs with this site open.');
            };
        });
    }

    /**
     * Normalize Vietnamese string (remove accents, lowercase, trim)
     */
    normalize(str) {
        if (!str) return '';

        // Lowercase
        str = str.toLowerCase();

        // Remove Vietnamese accents
        str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        // Replace đ with d
        str = str.replace(/đ/g, 'd');

        // Trim whitespace
        str = str.trim();

        // Replace multiple spaces with single space
        str = str.replace(/\s+/g, ' ');

        return str;
    }

    /**
     * Create search blob for product
     */
    createSearchBlob(product) {
        const nameUnsigned = this.normalize(product.title || '');
        const code = (product.code || '').toLowerCase();
        const attributeUnsigned = this.normalize(product.attribute_label || '');
        const nameNoSpace = nameUnsigned.replace(/\s/g, '');

        // Combine all search fields
        return `${nameUnsigned} ${code} ${attributeUnsigned} ${nameNoSpace}`;
    }

    /**
     * Save or update a product
     */
    async saveProduct(product) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readwrite');
            const store = transaction.objectStore(STORE_PRODUCTS);

            // Prepare product data
            const productData = {
                id: product.id,
                code: product.code || '',
                title: product.title || '',
                attribute_label: product.attribute_label || '',
                price: product.price || 0,
                type: product.type || 'product',
                parent_id: product.parent_id || 0,
                hasVariation: product.hasVariation || 0,
                vat: product.vat || 0,
                unit: product.unit || 'item',
                name_unsigned: this.normalize(product.title || ''),
                search_blob: this.createSearchBlob(product),
            };

            const request = store.put(productData);

            request.onsuccess = () => resolve(productData);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Save multiple products
     */
    async saveProducts(products) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readwrite');
            const store = transaction.objectStore(STORE_PRODUCTS);

            let completed = 0;
            const total = products.length;

            if (total === 0) {
                resolve(0);
                return;
            }

            products.forEach(product => {
                const productData = {
                    id: product.id,
                    code: product.code || '',
                    title: product.title || '',
                    attribute_label: product.attribute_label || '',
                    price: product.price || 0,
                    type: product.type || 'product',
                    parent_id: product.parent_id || 0,
                    hasVariation: product.hasVariation || 0,
                    vat: product.vat || 0,
                    unit: product.unit || 'item',
                    name_unsigned: this.normalize(product.title || ''),
                    search_blob: this.createSearchBlob(product),
                };

                const request = store.put(productData);

                request.onsuccess = () => {
                    completed++;
                    if (completed === total) {
                        resolve(completed);
                    }
                };

                request.onerror = () => reject(request.error);
            });
        });
    }

    /**
     * Delete a product
     */
    async deleteProduct(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readwrite');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.delete(id);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Search products with priority:
     * Level 1: Code exact match
     * Level 2: Name prefix match
     * Level 3: Search blob contains match
     */
    async searchProducts(keyword, limit = 50) {
        if (!this.db) await this.init();

        // If no keyword, return all products up to limit
        if (!keyword || keyword.trim() === '') {
            return new Promise((resolve, reject) => {
                const transaction = this.db.transaction([STORE_PRODUCTS], 'readonly');
                const store = transaction.objectStore(STORE_PRODUCTS);
                const request = store.getAll();

                request.onsuccess = () => {
                    const products = request.result.slice(0, limit);
                    resolve(products);
                };

                request.onerror = () => reject(request.error);
            });
        }

        const normalized = this.normalize(keyword);
        const lowerKeyword = keyword.toLowerCase();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readonly');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.getAll();

            request.onsuccess = () => {
                const products = request.result;

                const level1 = []; // Exact code match
                const level2 = []; // Prefix name match
                const level3 = []; // Contains match

                products.forEach(product => {
                    // Level 1: Exact code match
                    if (product.code && product.code.toLowerCase() === lowerKeyword) {
                        level1.push({ ...product, matchLevel: 1 });
                        return;
                    }

                    // Level 2: Prefix match
                    if (product.name_unsigned && product.name_unsigned.startsWith(normalized)) {
                        level2.push({ ...product, matchLevel: 2 });
                        return;
                    }

                    if (product.code && product.code.toLowerCase().startsWith(lowerKeyword)) {
                        level2.push({ ...product, matchLevel: 2 });
                        return;
                    }

                    // Level 3: Contains match
                    if (product.search_blob && product.search_blob.includes(normalized)) {
                        level3.push({ ...product, matchLevel: 3 });
                    }
                });

                // Combine results with priority
                const results = [...level1, ...level2, ...level3].slice(0, limit);
                resolve(results);
            };

            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get all products
     */
    async getAllProducts() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readonly');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get product by ID
     */
    async getProduct(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readonly');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.get(id);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clear all products
     */
    async clearProducts() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readwrite');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get meta value
     */
    async getMeta(key) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_META], 'readonly');
            const store = transaction.objectStore(STORE_META);
            const request = store.get(key);

            request.onsuccess = () => {
                const result = request.result;
                resolve(result ? result.value : null);
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Set meta value
     */
    async setMeta(key, value) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_META], 'readwrite');
            const store = transaction.objectStore(STORE_META);
            const request = store.put({ key, value });

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get product count
     */
    async getProductCount() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_PRODUCTS], 'readonly');
            const store = transaction.objectStore(STORE_PRODUCTS);
            const request = store.count();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * ========== Offline Invoices Methods ==========
     */

    /**
     * Save offline invoice (tabs data)
     */
    async saveOfflineInvoice(invoiceData) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_OFFLINE_INVOICES], 'readwrite');
            const store = transaction.objectStore(STORE_OFFLINE_INVOICES);

            const data = {
                id: 'current_invoice', // Always use same key to override
                tabs: invoiceData.tabs,
                activeTab: invoiceData.activeTab,
                nextTabId: invoiceData.nextTabId,
                timestamp: Date.now(),
            };

            const request = store.put(data);

            request.onsuccess = () => resolve(data);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get offline invoice
     */
    async getOfflineInvoice() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_OFFLINE_INVOICES], 'readonly');
            const store = transaction.objectStore(STORE_OFFLINE_INVOICES);
            const request = store.get('current_invoice');

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Delete offline invoice
     */
    async deleteOfflineInvoice() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_OFFLINE_INVOICES], 'readwrite');
            const store = transaction.objectStore(STORE_OFFLINE_INVOICES);
            const request = store.delete('current_invoice');

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Clear all offline invoices
     */
    async clearOfflineInvoices() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([STORE_OFFLINE_INVOICES], 'readwrite');
            const store = transaction.objectStore(STORE_OFFLINE_INVOICES);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Delete and recreate database (for troubleshooting)
     */
    async resetDatabase() {
        // Close current connection
        if (this.db) {
            this.db.close();
            this.db = null;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(DB_NAME);

            request.onsuccess = () => {
                console.log('✅ Database deleted successfully');
                // Reinitialize
                this.init()
                    .then(() => {
                        console.log('✅ Database recreated successfully');
                        resolve();
                    })
                    .catch(reject);
            };

            request.onerror = () => {
                console.error('❌ Failed to delete database:', request.error);
                reject(request.error);
            };

            request.onblocked = () => {
                console.warn('⚠️ Database deletion blocked. Please close all tabs.');
                reject(new Error('Database deletion blocked'));
            };
        });
    }
}

// Export singleton instance
const indexedDBService = new IndexedDBService();

export default indexedDBService;
